'use client';

import type { CSSProperties } from 'react';
import { useRef } from 'react';
import { clamp } from '../core/math';
import { useElementWidth } from '../core/useElementWidth';
import type { Sample } from '../irregularsample/IrregularSample';
import styles from './RangeColumns.module.css';

const CAPSULE_WIDTH = 4;
const BUCKET_PITCH = 8; // px between capsule centers

const niceTicks = (min: number, max: number, target = 3): number[] => {
  const raw = Math.max(1e-9, max - min) / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) ticks.push(v);
  return ticks;
};

export const RangeColumns = ({
  samples,
  overlay,
  overlayColor = 'rgba(255, 255, 255, 0.55)',
  domain,
  height = 120,
  color,
  className,
  ariaLabel,
}: {
  samples: Sample[];
  overlay?: Sample[];
  overlayColor?: string;
  domain?: [number, number];
  height?: number;
  color?: string;
  className?: string;
  ariaLabel?: string;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(wrapRef);

  let min = Infinity;
  let max = -Infinity;
  let minT = Infinity;
  let maxT = -Infinity;
  for (const s of samples) {
    if (s.value < min) min = s.value;
    if (s.value > max) max = s.value;
    if (s.t < minT) minT = s.t;
    if (s.t > maxT) maxT = s.t;
  }
  for (const s of overlay ?? []) {
    if (s.value < min) min = s.value;
    if (s.value > max) max = s.value;
  }
  if (domain) [minT, maxT] = domain;

  const spanT = Math.max(1e-9, maxT - minT);
  const pad = Math.max(1e-9, max - min) * 0.15;
  const displayMax = max + pad;
  const displayMin = Math.max(0, min - pad);
  const y = (v: number) =>
    ((displayMax - v) / Math.max(1e-9, displayMax - displayMin)) * height;

  const bucketCount = Math.max(
    1,
    Math.floor((width - CAPSULE_WIDTH) / BUCKET_PITCH)
  );
  const lo = new Array<number>(bucketCount).fill(Infinity);
  const hi = new Array<number>(bucketCount).fill(-Infinity);
  for (const s of samples) {
    const frac = (s.t - minT) / spanT;
    if (frac < 0 || frac > 1) continue;
    const b = clamp(Math.floor(frac * bucketCount), 0, bucketCount - 1);
    if (s.value < lo[b]) lo[b] = s.value;
    if (s.value > hi[b]) hi[b] = s.value;
  }

  const bucketWidth = width / bucketCount;
  const capsules: { x: number; y: number; h: number }[] = [];
  for (let i = 0; i < bucketCount; i++) {
    if (hi[i] < lo[i]) continue;
    const top = y(hi[i]);
    const h = Math.max(CAPSULE_WIDTH, y(lo[i]) - top);
    capsules.push({
      x: (i + 0.5) * bucketWidth - CAPSULE_WIDTH / 2,
      y: top,
      h,
    });
  }

  const ticks = width > 0 ? niceTicks(displayMin, displayMax) : [];

  const overlayPoints = (overlay ?? [])
    .filter((s) => s.t >= minT && s.t <= maxT)
    .sort((a, b) => a.t - b.t)
    .map((s) => `${((s.t - minT) / spanT) * width},${y(s.value)}`)
    .join(' ');

  return (
    <div
      className={`${styles.wrap}${className ? ` ${className}` : ''}`}
      ref={wrapRef}
      role="img"
      aria-label={ariaLabel ?? 'Value ranges over time'}
      style={color ? ({ '--color': color } as CSSProperties) : undefined}
    >
      {width > 0 && (
        <svg width={width} height={height} className={styles.svg}>
          {ticks.map((v) => (
            <line
              key={v}
              className={styles.guide}
              x1={0}
              y1={y(v)}
              x2={width}
              y2={y(v)}
              strokeWidth={1}
              strokeDasharray="3"
            />
          ))}
          {capsules.map((c, i) => (
            <rect
              key={i}
              className={styles.capsule}
              x={c.x}
              y={c.y}
              width={CAPSULE_WIDTH}
              height={c.h}
              rx={CAPSULE_WIDTH / 2}
            />
          ))}
          {overlayPoints && (
            <polyline
              points={overlayPoints}
              fill="none"
              stroke={overlayColor}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      )}
    </div>
  );
};
